import styled from '@emotion/styled'
import { colors } from '../../__styling/settings/colors'
import { fonts } from '../../__styling/settings/fonts'
import { IconSearch } from '../../icons/IconSearch/IconSearch'
import { calcRem } from '../../__styling/style-functions/calc-rem'
import { breakpoint } from '../../__styling/style-functions'
import { IconCross } from '../../icons/IconCross/IconCross'

interface StyledSearchContentProps {
    searchInMainNav?: boolean
    searchHasValue: boolean
}

interface StyledSearchSubmitButtonProps {
    disabled: boolean
    isLarge: boolean
}

interface StyledClearButtonProps {
    searchHasValue: boolean
    isLarge: boolean
}

export const StyledSearchContainer = styled('div')<StyledSearchContentProps>(
    ({ searchInMainNav }) => [
        {
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
        },
        searchInMainNav && {
            display: 'none',

            [breakpoint('sm')]: {
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
            },
        },
    ],
)

export const StyledSearchIcon = styled(IconSearch)(({ theme }) => ({
    width: calcRem(14),
    height: calcRem(14),
    fill: theme.sectionValues.primaryColor,
    display: 'flex',
}))
StyledSearchIcon.displayName = 'SearchIcon'

export const StyledCrossIcon = styled(IconCross)({
    height: calcRem(10),
    width: calcRem(10),
    fill: colors.thewest.greyGorilla,
})
StyledCrossIcon.displayName = 'IconCross'

interface StyledSearchFormProps {
    searchHasFocus: boolean
    searchHasValue: boolean
    isLarge: boolean
}

export const StyledSearchForm = styled('form')<StyledSearchFormProps>(
    ({ isLarge, searchHasFocus, searchHasValue }) => ({
        display: 'flex',
        padding: isLarge ? calcRem(6, 4, 6, 10) : calcRem(1, 1, 1, 10),
        fontSize: isLarge ? calcRem(16) : calcRem(13),

        margin: 0,
        borderRadius: '1rem',
        overflow: 'hidden',
        width: '100%',
        background: colors.thewest.greyErmine,
        border:
            searchHasFocus || searchHasValue
                ? `1px solid ${colors.thewest.greyDolphin}`
                : '1px solid transparent',
        color: searchHasFocus ? colors.black : colors.thewest.greyElephant,
    }),
)

StyledSearchForm.displayName = 'SearchForm'

export const StyledSearchInput = styled('input')({
    width: '100%',
    border: 'none',
    fontFamily: fonts.thewest.sansSerif,
    fontWeight: 600,
    background: 'transparent',

    '&:focus': {
        outline: 'none',
    },

    '&::placeholder': {
        color: colors.thewest.greyElephant,
        opacity: 1,
    },

    // We have our own cancel, so we end up with 2 in chrome and edge.
    '&::-webkit-search-cancel-button': {
        display: 'none',
    },
})

StyledSearchInput.displayName = 'SearchInput'

export const StyledClearButton = styled('button')<StyledClearButtonProps>(
    ({ searchHasValue, theme, isLarge }) => [
        {
            padding: isLarge ? calcRem(5, 10) : calcRem(2, 6),
            background: 'transparent',
            cursor: 'pointer',
            border: 'none',
            lineHeight: 0,
            marginRight: calcRem(5),

            '&:hover path, &:focus path': {
                fill: theme.sectionValues.primaryColor,
            },

            [breakpoint('md')]: {
                marginRight: 0,
            },
        },
        !searchHasValue && {
            visibility: 'hidden',
        },
    ],
)
StyledClearButton.displayName = 'ClearButton'

export const StyledSearchSubmitButton = styled('button')<
    StyledSearchSubmitButtonProps
>(({ theme, isLarge }) => ({
    padding: isLarge ? calcRem(5, 10) : calcRem(2, 6),
    border: 'none',
    borderRadius: calcRem(2),
    cursor: 'pointer',
    margin: calcRem(1),
    outline: 'none',
    backgroundColor: 'transparent',

    [`${StyledSearchIcon}`]: {
        fill: colors.thewest.greyElephant,
    },

    '&:disabled': {
        cursor: 'not-allowed',

        [`${StyledSearchIcon}`]: {
            fill: colors.thewest.greyElephant,
        },
    },

    [`&:hover:disabled ${StyledSearchIcon}`]: {
        fill: colors.thewest.greyElephant,
    },

    [`&:hover ${StyledSearchIcon}, &:focus ${StyledSearchIcon}`]: {
        fill: theme.sectionValues.primaryColor,
    },
}))
StyledSearchSubmitButton.displayName = 'SubmitButton'
